import { verifySession } from '../utils/session';
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import Message from '../components/Message';
import { handleTokenInvalidation } from '../utils/tokenHandler';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);

  // 初始化时从localStorage恢复用户状态
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        verifySession(token);
      } catch (error) {
        console.error('解析用户数据失败:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
    setInitialized(true);
    const invalid = () => setUser(null);
    window.addEventListener('pb-session-invalid', invalid);
    try {
      const notice = sessionStorage.getItem('pb-login-notice');
      if (notice) { sessionStorage.removeItem('pb-login-notice'); Message.error(notice); }
    } catch {}
    return () => window.removeEventListener('pb-session-invalid', invalid);
  }, []);

  // 在初始化完成之前不渲染任何内容
  if (!initialized) {
    return null;
  }

  const updateUser = (newUserData) => {
    setUser(prev => ({
      ...prev,
      ...newUserData
    }));
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const getRandomAvatar = () => {
    const totalAvatars = 34; // 总共34个默认头像
    const randomIndex = Math.floor(Math.random() * totalAvatars) + 1;
    return `/avatars/${randomIndex}.png`;
  };

  // 登录
  const login = async (userName, password) => {
    try {
      const response = await fetch('//server.battleverse.cn/pub/login.do', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          loginName: userName,
          password: password
        })
      });

      const data = await response.json();
      if (data.success && data.code === 0) {
        const userData = data.data.user;
        const token = data.data.token;

        // 保存用户信息和token
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);

        return true;
      } else {
        throw new Error(data.msg || '登录失败');
      }
    } catch (error) {
      console.error('登录错误:', error);
      throw error;
    }
  };

  // 获取当前token
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // 更新用户信息和token
  const updateUserAndToken = (userData, token) => {
    if (userData) {
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    }
    if (token) {
      localStorage.setItem('token', token);
    }
  };

  // 修改头像（上传）
  const updateAvatarByUpload = async (file) => {
    const formData = new FormData();
    formData.append('upfile', file);

    try {
      const response = await fetch('//server.battleverse.cn/user/pic/upload.do', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success && data.code === 0) {
        const { user: updatedUser, token } = data.data;
        await updateUserAndToken(updatedUser, token);
        localStorage.setItem('userPic', updatedUser.userPic);
        Message.success('头像更新成功');
        return true;
      } else if (data.code === -2) {
        // 处理token无效的情况
        handleTokenInvalidation(data.msg || '登录已失效，请重新登录');
        return false;
      } else {
        throw new Error(data.msg || '更新头像失败');
      }
    } catch (error) {
      Message.error(error.message || '系统异常，请稍后重试');
      return false;
    }
  };

  // 修改昵称
  const updateUsername = async (userName) => {
    try {
      const response = await fetch('//server.battleverse.cn/user/update/name.do', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userName })
      });

      const data = await response.json();
      if (data.success && data.code === 0) {
        updateUserAndToken(data.data.user, data.data.token);
        Message.success('昵称修改成功');
        return true;
      } else if (data.code === -2) {
        // 处理token无效的情况
        handleTokenInvalidation(data.msg || '登录已失效，请重新登录');
        return false;
      } else {
        throw new Error(data.msg || '修改昵称失败');
      }
    } catch (error) {
      Message.error(error.message || '系统异常，请稍后重试');
      return false;
    }
  };

  // 检查登录状态
  const checkLoginStatus = () => {
    const token = getToken();
    return !!token;
  };

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        getToken,
        updateAvatarByUpload,
        updateUsername,
        checkLoginStatus,
        updateUserAndToken
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
